/* eslint-disable react/jsx-max-props-per-line */
import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const metricButtonSx = {
  border: 0,
  bgcolor: 'transparent',
  cursor: 'pointer',
  p: 0,
  font: 'inherit',
  fontSize: 'inherit',
  lineHeight: 'inherit',
};

const MetricButton = ({ children, onClick, color = 'text.secondary' }) => (
  <Typography
    variant="inherit"
    component="button"
    type="button"
    onClick={onClick}
    color={color}
    sx={metricButtonSx}
  >
    {children}
  </Typography>
);

MetricButton.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  color: PropTypes.string,
};

MetricButton.defaultProps = {
  onClick: undefined,
  color: 'text.secondary',
};

const DigitacaoResumoStrip = ({
  resumo,
  mode,
  compactAdminView,
  showRelatorioLink,
  digitadorAtualNome,
  onFilterIntegracao,
  onFilterErro,
}) => {
  const isConsulta = mode === 'CONSULTA';
  const total = resumo?.totalServicos ?? 0;
  const digitados = isConsulta ? (resumo?.meusDigitados ?? 0) : (resumo?.digitados ?? 0);
  const pendentes = isConsulta ? null : (resumo?.pendentes ?? 0);
  const integradas = isConsulta ? (resumo?.meusIntegrados ?? 0) : (resumo?.integradas ?? 0);
  const erros = isConsulta ? (resumo?.meusErroIntegracao ?? 0) : (resumo?.erroIntegracao ?? 0);
  const semIntegracao = isConsulta ? null : (resumo?.digitadoSemIntegracao ?? 0);
  const pct = total > 0 && !isConsulta ? Math.round((digitados / total) * 100) : 0;
  const title = isConsulta ? 'Minha participação nesta NSIR' : 'Esta NSIR';

  const renderMetrics = (variant = 'body2') => (
    <Stack
      direction="row"
      spacing={1.5}
      flexWrap="wrap"
      useFlexGap
      divider={
        <Typography variant={variant} color="text.disabled" component="span">
          ·
        </Typography>
      }
    >
      <MetricButton onClick={onFilterIntegracao}>
        Integradas {integradas}
      </MetricButton>
      <MetricButton
        onClick={onFilterErro}
        color={erros > 0 ? 'error.main' : 'text.secondary'}
      >
        Erro integração {erros}
      </MetricButton>
      {!isConsulta && semIntegracao > 0 && (
        <Typography variant={variant} color="text.secondary" component="span">
          Sem integração {semIntegracao}
        </Typography>
      )}
    </Stack>
  );

  return (
    <Box
      sx={{
        mb: 2,
        py: 1,
        px: 1.5,
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Stack spacing={0.75}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
        >
          <Typography variant="subtitle2" color="text.secondary">
            {title}
          </Typography>
          {showRelatorioLink && (
            <Button
              component={RouterLink}
              to="/v2/minha-digitacao"
              variant="outlined"
              size="small"
              sx={{ flexShrink: 0 }}
            >
              Ver relatório
            </Button>
          )}
        </Stack>

        {compactAdminView && !isConsulta && (
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            alignItems="center"
            sx={{ typography: 'body2', fontWeight: 600 }}
          >
            <Typography variant="body2" fontWeight={600} component="span">
              {digitados}/{total} digitados · {pendentes} pendentes
            </Typography>
            <Typography variant="body2" color="text.disabled" component="span">
              ·
            </Typography>
            {renderMetrics('body2')}
          </Stack>
        )}

        {!compactAdminView && !isConsulta && (
          <>
            <Typography variant="body2" fontWeight={600}>
              {digitados} / {total} serviços digitados · {pendentes} pendentes
            </Typography>
            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{ height: 4, borderRadius: 1 }}
            />
            {renderMetrics()}
          </>
        )}

        {isConsulta && (
          <>
            <Typography variant="body2" fontWeight={600}>
              {digitados} serviço(s) digitados por você
            </Typography>
            {renderMetrics()}
            {digitadorAtualNome && (
              <Typography variant="caption" color="text.secondary">
                Digitador atual: {digitadorAtualNome}
              </Typography>
            )}
          </>
        )}
      </Stack>
    </Box>
  );
};

DigitacaoResumoStrip.propTypes = {
  resumo: PropTypes.object,
  mode: PropTypes.string,
  compactAdminView: PropTypes.bool,
  showRelatorioLink: PropTypes.bool,
  digitadorAtualNome: PropTypes.string,
  onFilterIntegracao: PropTypes.func,
  onFilterErro: PropTypes.func,
};

DigitacaoResumoStrip.defaultProps = {
  resumo: {},
  mode: 'ATIVO',
  compactAdminView: false,
  showRelatorioLink: false,
  digitadorAtualNome: null,
  onFilterIntegracao: undefined,
  onFilterErro: undefined,
};

export default DigitacaoResumoStrip;
